import { useCallback, useEffect, useState } from 'react';
import { getInvoiceStorageRecord } from 'services/billing/storageBillingInvoices.service';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { ICountryDetail } from '../../../types/countries';
import { BordInvoiceInfo } from 'components/BillingDataTab/Components/BordInvoiceInfo/BordInvoiceInfo';
import useResetData from 'hooks/useResetData';
import InvoiceRow from '../InvoiceRow/InvoiceRow';
import { IInvoiceStorageRecord, INVOICE_SECTION_VARIANT } from '../InvoiceSectionOfBillingTab.types';
import './InvoiceSectionStorage.scss';

interface IInvoiceSectionStorageProps {
  storageInvoiceId: number;
  companyName: string;
  emissionCountry: string;
  emissionCountryFlag?: string;
  billingCountry?: ICountryDetail;
  hasIVA: boolean;
  billingProvider?: string;
  disabled?: boolean;
}

const InvoiceSectionStorage = ({
  storageInvoiceId,
  companyName,
  emissionCountry,
  emissionCountryFlag,
  billingCountry,
  hasIVA,
  billingProvider,
  disabled
}: IInvoiceSectionStorageProps) => {
  const [invoiceRecords, setInvoiceRecords] = useState<IInvoiceStorageRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { resetStorageDataWithLoadersState } = useResetData();

  const getInvoiceRecords = useCallback(async () => {
    if (!storageInvoiceId) return;

    try {
      setIsLoading(true);
      const currInvoiceRecords = await getInvoiceStorageRecord(storageInvoiceId);
      if (!currInvoiceRecords || currInvoiceRecords?.length === 0) {
        setInvoiceRecords([{ type: INVOICE_SECTION_VARIANT.STORAGE, storageInvoiceId }]);
        return;
      }
      setInvoiceRecords(currInvoiceRecords);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
    }
  }, [storageInvoiceId]);

  const updateCurrentInvoiceRecord = useCallback((invoiceRecord: IInvoiceStorageRecord, index: number) => {
    setInvoiceRecords(prevRecords => [...prevRecords.slice(0, index), invoiceRecord, ...prevRecords.slice(index + 1)]);
  }, []);

  const createHandleUpdate = (index: number) => (updatedRecord: IInvoiceStorageRecord) => {
    updateCurrentInvoiceRecord(updatedRecord, index);
  };

  useEffect(() => {
    getInvoiceRecords();
  }, [getInvoiceRecords, resetStorageDataWithLoadersState]);

  return (
    <section className="invoiceSectionStorage">
      <div className="invoiceSectionStorageHeader">
        <p className="sectionTitle">Factura emitida por Bord</p>
      </div>
      <div className="internalDivider" />
      <div className="invoiceSectionStorageCard">
        <BordInvoiceInfo
          beneficiary={companyName || ''}
          countryName={emissionCountry || ''}
          countryEmissionFlag={emissionCountryFlag || ''}
          billingCountry={billingCountry}
          isInvoicingWithIVA={hasIVA}
          billingProvider={billingProvider}
        />
        {isLoading ? (
          <div className="invoiceRowContainerStorageSkeleton animationLoader" />
        ) : (
          invoiceRecords?.map((invoiceRecord, index) => (
            <div key={`invoice-storage-${index}`} className="invoiceRowContainerStorage">
              <InvoiceRow
                billingProvider={billingProvider}
                disabled={disabled}
                invoiceRecord={{ ...invoiceRecord, type: INVOICE_SECTION_VARIANT.STORAGE }}
                handleUpdateCurrentInvoiceRecord={createHandleUpdate(index)}
                customClassNameRow="customClassNameRow"
              />
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default InvoiceSectionStorage;
